import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useLoginUserMutation } from "../features/auth/authApi";
import { setUser } from "../features/auth/authSlice";
import PageTransition from "../components/PageTransition";

interface LoginPageProps {
  [key: string]: any;
}

interface LoginPageState {
  email: any;
  password: any;
  showPassword: any;
  agreedToTerms: any;
}

function LoginPage(props: LoginPageProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [login, { isLoading, isError, error }]: any = useLoginUserMutation();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert(
        "Please agree to the Terms of Service and Data Processing Agreement"
      );
      return;
    }
    try {
      const response: any = await login({ email, password }).unwrap();
      console.log("✅ Login API response:", response);
      console.log("✅ User email sent:", email);

      // ✅ Dispatch user to Redux store
      dispatch(
        setUser({
          user: { email },
          role: response?.role || null, // if backend returns a role
        })
      );

      console.log("✅ Dispatched user to Redux:", { email });

      // Redirect after successful login
      navigate("/", { state: { fromLogin: true } });
    } catch (err: any) {
      console.error("❌ Login failed:", err);
      alert(err?.data?.message || err?.error || "Login failed");
    }
  };

  return (
    <PageTransition>
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex w-[374px] flex-col items-center gap-[36px] flex-shrink-0">
          <img
            src="/assets/images/kimaru.png"
            alt="kimaru.ai logo"
            className="w-[232px] h-[63px]"
          />
          <form
            onSubmit={handleSubmit}
            className="flex flex-col w-full items-start gap-[24px] p-[24px] rounded-lg border border-[#E4E4E7] bg-white shadow-md"
          >
            <h2 className="text-[#09090B] font-sans text-2xl font-semibold leading-8 w-full">
              Login
            </h2>
            <p className="text-[#64748B] font-sans text-sm font-normal leading-5 w-full">
              Enter your email and password to log in to your account.
            </p>

            {/* Email */}
            <div className="flex flex-col w-full gap-[8px]">
              <label
                htmlFor="email"
                className="text-[#111827] font-sans text-sm font-medium w-full"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                placeholder="monica@kimaru.ai"
                className="h-10 px-3 py-2 w-full rounded-md border border-[#E2E8F0] bg-white text-sm"
              />
            </div>

            {/* Password */}
            <div className="flex flex-col w-full gap-[8px]">
              <label
                htmlFor="password"
                className="text-[#111827] font-sans text-sm font-medium w-full"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-10 px-3 py-2 w-full rounded-md border border-[#E2E8F0] bg-white text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start w-full gap-2 mt-2">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={agreedToTerms}
                onChange={(e: any) => setAgreedToTerms(e.target.checked)}
                className="mt-1"
              />
              <label
                htmlFor="terms-checkbox"
                className="text-[#64748B] font-sans text-sm"
              >
                I confirm that I have read and agree to the{" "}
                <Link
                  to="/license-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and the{" "}
                <Link
                  to="/data-processing-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Data Processing Agreement
                </Link>
                .
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || !agreedToTerms}
              className={`flex h-10 w-full items-center justify-center gap-2 rounded-md ${agreedToTerms
                ? "bg-[#0F172A] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                } font-semibold text-sm`}
            >
              {isLoading ? "Logging in..." : "Sign In"}
            </button>
            {isError && (
              <p style={{ color: "red" }}>
                {error?.data?.message || error?.error || "Error"}
              </p>
            )}
          </form>
        </div>
      </div>
    </PageTransition>
  );
}

export default LoginPage;
